import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Languages, FileEdit, FileText, CalendarDays, MessageSquare, Home, LogOut } from "lucide-react";
import { useClerk } from '@clerk/clerk-react';
import logo from "../assets/logo.png";


const SidebarLink = ({ to, icon: Icon, label }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-150 ${
        isActive 
          ? "bg-white/50 backdrop-blur-xl text-gray-800 shadow-sm border border-white/40" 
          : "text-gray-700 hover:bg-white/30 hover:text-gray-800"
      }`
    }
    end
  >
    <Icon size={18} />
    <span className="font-medium">{label}</span>
  </NavLink>
);

const Dashboard = () => {
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="h-screen relative overflow-hidden">
      {/* Background matching home page */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(24,24,27,0.35)_0%,rgba(244,244,245,0.9)_42%,rgba(255,255,255,1)_50%,rgba(244,244,245,0.9)_58%,rgba(24,24,27,0.35)_100%)]"></div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-zinc-700/35 via-zinc-600/20 to-transparent"></div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-zinc-700/35 via-zinc-600/20 to-transparent"></div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-zinc-500/15 to-transparent"></div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-52 bg-gradient-to-t from-zinc-500/20 to-transparent"></div>
        <div className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-white/60 blur-2xl"></div>
        <div className="pointer-events-none absolute right-0 top-0 h-[560px] w-[560px] rounded-full bg-white/50 blur-2xl translate-x-1/3 -translate-y-1/3"></div>
      </div>

      <div className="flex h-full relative z-10">
        {/* Sidebar - Fixed width on extreme left */}
        <aside className="w-64 bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border-r border-white/30 shadow-[0_10px_25px_rgba(0,0,0,0.08)] flex flex-col h-full">
          {/* Logo/Brand */}
          <div className="flex items-center justify-center p-6 border-b border-white/30">
            <img src={logo} alt="Legaily Logo" className="h-10 w-auto" />
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            <SidebarLink to="" icon={Home} label="Dashboard" />
            <SidebarLink to="translate" icon={Languages} label="Translate" />
            <SidebarLink to="drafts" icon={FileEdit} label="Drafts" />
            <SidebarLink to="docai" icon={FileText} label="Doc AI" />
            <SidebarLink to="available-documents" icon={FileText} label="Available Docs" />
            <SidebarLink to="adv-diary" icon={CalendarDays} label="Adv Diary" />
            <SidebarLink to="query" icon={MessageSquare} label="Query Bot" />
          </nav>
          
          {/* Sign Out */}
          <div className="p-4 border-t border-white/30">
            <button 
              onClick={handleSignOut}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-white/50 transition-colors duration-150 w-full"
            >
              <LogOut size={18} />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </aside>

        {/* Main content area - Takes remaining space */}
        <main className="flex-1 h-full overflow-y-auto">
          <div className="h-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;


